export type PlatformFlagsMap = {
    sms_login: boolean;
    bullion_env: boolean;
    sixes_env: boolean;
    new_registration_bonus: boolean;
    leagues_env: boolean;
};

const PLATFORM_FLAG_KEYS: Exclude<keyof PlatformFlagsMap, "leagues_env">[] = [
    "sms_login",
    "bullion_env",
    "sixes_env",
    "new_registration_bonus",
];

let runtimeFlags: PlatformFlagsMap | null = null;

export function setRuntimePlatformFlags(flags: PlatformFlagsMap) {
    runtimeFlags = flags;
}

function readEnvDefault(raw: string | undefined): boolean {
    if (raw == null || raw === "") return true;
    return raw.toLowerCase() !== "false";
}

/** Build-time defaults used until `/platform-flags` loads. */
export function getEnvFallbackPlatformFlags(): PlatformFlagsMap {
    return {
        sms_login: process.env.HIDE_LOGIN_WITH_SMS !== "true",
        bullion_env: readEnvDefault(process.env.BULLION_ENABLED),
        sixes_env: readEnvDefault(process.env.SIXES_ENABLED),
        new_registration_bonus: true,
        leagues_env: false,
    };
}

function effective(key: keyof PlatformFlagsMap): boolean {
    if (runtimeFlags) return runtimeFlags[key];
    return getEnvFallbackPlatformFlags()[key];
}

export function parsePlatformFlagsResponse(parsed: unknown): PlatformFlagsMap | null {
    if (!parsed || typeof parsed !== "object") return null;
    const data = (parsed as { data?: unknown }).data;
    if (!data || typeof data !== "object") return null;
    const record = data as Record<string, unknown>;
    const result = {} as PlatformFlagsMap;
    for (const key of PLATFORM_FLAG_KEYS) {
        if (typeof record[key] !== "boolean") return null;
        result[key] = record[key];
    }
    result.leagues_env = record.leagues_env === true;
    return result;
}

/** Mirrors backend `featureFlags.isSmsLoginEnabled`. */
export function isSmsLoginEnabled(): boolean {
    return effective("sms_login");
}

/** Mirrors backend `featureFlags.isBullionEnabled`. */
export function isBullionEnvEnabled(): boolean {
    return effective("bullion_env");
}

/** Mirrors backend `featureFlags.isSixesEnabled`. */
export function isSixesEnvEnabled(): boolean {
    return effective("sixes_env");
}

/** Mirrors backend `featureFlags.isNewRegistrationBonusEnabled`. */
export function isNewRegistrationBonusEnabled(): boolean {
    return effective("new_registration_bonus");
}

/**
 * Header / drawer active-game badge — exclude in-progress BULLION/SIXES when the platform
 * env flag is off. Pass `isBullionEnvEnabled()` / `isSixesEnvEnabled()` (same source as the
 * count API), not player lobby `live` flags from `useGamesEnabled` (those are false while
 * loading and will zero the badge).
 */
export function computeMyGamesBadgeCount(
    totalActiveGames: number,
    bullionActivePlaysCount: number | undefined,
    isBullionEnabled: boolean,
    sixesActiveCardsCount?: number | undefined,
    isSixesEnabled?: boolean,
): number {
    let total = Math.max(0, parseInt(String(totalActiveGames ?? 0), 10) || 0);
    if (!isBullionEnabled) {
        const bullion = Math.max(0, parseInt(String(bullionActivePlaysCount ?? 0), 10) || 0);
        total = Math.max(0, total - bullion);
    }
    if (isSixesEnabled === false) {
        const sixes = Math.max(0, parseInt(String(sixesActiveCardsCount ?? 0), 10) || 0);
        total = Math.max(0, total - sixes);
    }
    return total;
}
